// Matriz tipo × gama: qué reglas activas caen en cada combinación. Mockup: portal-admin-vl-comisiones.html
import { Fragment } from 'react';
import type { RetailCommission } from '@fiado/type-kit';
import { cn } from '@/lib/cn';
import { MasterDetailEmpty } from '@/shared/patterns/MasterDetail';
import { buildMatrix, countTone, MATRIX_COLUMNS } from '../../lib/ruleMatrix';
import { describeCondition } from '../../lib/ruleText';

const COUNT_CLASS = {
    success: 'text-success',
    warning: 'text-warning',
    danger: 'text-destructive',
} as const;

export function RuleMatrix({ rules }: { rules: RetailCommission.CommissionRule[] }) {
    const matrix = buildMatrix(rules);
    if (matrix.groups.length === 0) {
        return (
            <MasterDetailEmpty
                title="Sin reglas activas"
                sub="La matriz muestra las reglas activas por tipo y gama de producto."
            />
        );
    }
    return (
        <div className="min-h-0 flex-1 overflow-auto p-4">
            <p className="mb-3 text-[11px] text-muted-foreground">
                Qué reglas activas disparan en cada combinación tipo × gama. Solo mira tipos y
                gamas: SKUs, tiendas y condición no entran.
            </p>
            <table className="w-full border-collapse text-[11px]">
                <caption className="sr-only">Matriz de reglas por tipo y gama</caption>
                <thead>
                    <tr>
                        <th className="border-b border-border px-2 py-1 text-left">Regla</th>
                        {MATRIX_COLUMNS.map((column) => (
                            <th
                                key={column.key}
                                className="border-b border-border px-2 py-1 text-center"
                            >
                                {column.label}
                            </th>
                        ))}
                    </tr>
                </thead>
                <tbody>
                    {matrix.groups.map(({ group, rows }) => (
                        <Fragment key={group}>
                            <tr>
                                <th
                                    colSpan={MATRIX_COLUMNS.length + 1}
                                    className="bg-muted/50 px-2 py-1 text-left text-[10px] uppercase"
                                >
                                    {group}
                                </th>
                            </tr>
                            {rows.map(({ rule, cells }) => (
                                <tr key={rule.ruleId} className="border-b border-border">
                                    <td className="px-2 py-1">
                                        <div className="font-medium">{rule.name}</div>
                                        <div className="text-[10px] text-muted-foreground">
                                            {describeCondition(rule.condition, rule.countBy)}
                                        </div>
                                    </td>
                                    {cells.map((cell, index) => (
                                        <td
                                            key={MATRIX_COLUMNS[index].key}
                                            className={cn(
                                                'px-2 py-1 text-center',
                                                cell
                                                    ? 'bg-success/10 text-success'
                                                    : 'text-muted-foreground/50',
                                            )}
                                        >
                                            {cell ?? '—'}
                                        </td>
                                    ))}
                                </tr>
                            ))}
                        </Fragment>
                    ))}
                    <tr>
                        <th className="px-2 py-1 text-left">Reglas que caen en esta combinación</th>
                        {matrix.counts.map((count, index) => (
                            <td
                                key={MATRIX_COLUMNS[index].key}
                                className={cn(
                                    'px-2 py-1 text-center font-semibold',
                                    COUNT_CLASS[countTone(count)],
                                )}
                            >
                                {count}
                            </td>
                        ))}
                    </tr>
                </tbody>
            </table>
        </div>
    );
}
